import { useEffect, useState } from 'react'

import {
    Checkbox,
    CompanyAccountActivityStatusFilter,
    CompanyFilter,
    DatePicker,
    FormControl,
    Input,
    InverterManufacturerFilter,
    MonitoringStatusDropdown,
    ProductFilter,
    SearchableSelect,
    WarrantyProductTypeFilter
} from '@/components'
import { UserRoleEnum } from '@/enums'
import { useAuth } from '@/hooks'
import { Company } from '@/models'
import {
    AccountManagerDataType,
    FormChangeEvent,
    FormChangeEventHandler,
    MonitoringIndexFiltersType,
    MonitoringSummaryDataType
} from '@/types'

type MonitoringIndexFiltersProps = {
    standalone?: boolean
    filters: MonitoringIndexFiltersType
    summary: MonitoringSummaryDataType

    onChange: FormChangeEventHandler
}

export const MonitoringIndexFilters = ({
    standalone,
    filters,
    summary,
    ...props
}: MonitoringIndexFiltersProps) => {
    const auth = useAuth()
    const [accountManagers, setAccountManagers] = useState<AccountManagerDataType[]>([])

    const fetchAccountManagers = async () => {
        const res = await Company.accountManagers()
        setAccountManagers(res.data)
    }

    useEffect(() => {
        if (auth.user?.role.isAdminOrStaffOrAdvisor) {
            fetchAccountManagers()
        }
    }, [])

    const handleChange = (e: FormChangeEvent) => {
        const { name, value, checked } = e.target
        if (name === 'with_emailed_at') {
            let displayValue = ''
            const newValue = checked ? value : ''
            if (newValue === '1') {
                displayValue = 'Yes'
            } else if (newValue === '0') {
                displayValue = 'No'
            }
            props.onChange({
                target: {
                    name,
                    value: newValue,
                    checked,
                    displayValue
                }
            })
        } else if(['size_kw_min', 'size_kw_max'].includes(name)) {
            props.onChange({
                target: {
                    name,
                    value,
                    displayValue: `${value} kW`
                }
            })
        } else if (['battery_size_kwh_min', 'battery_size_kwh_max'].includes(name)) {
            props.onChange({
                target: {
                    name,
                    value,
                    displayValue: `${value} kWh`
                }
            })
        } else {
            props.onChange(e)
        }
    }

    useEffect(() => {
        if (!standalone) {
            if (filters.with_emailed_at.value !== '1' && filters.emailed_at_range.value.some(item => item)) {
                setTimeout(() => {
                    props.onChange({
                        target: {
                            name: 'emailed_at_range',
                            value: filters.emailed_at_range.defaultValue,
                            displayValue: ''
                        }
                    })
                })
            }
            if (filters.with_emailed_at.value && !filters.with_emailed_at.displayValue) {
                props.onChange({
                    target: {
                        name: 'with_emailed_at',
                        value: filters.with_emailed_at.value,
                        checked: true,
                        displayValue: filters.with_emailed_at.value === '1' ? 'Yes' : 'No'
                    }
                })
            }
        }
    }, [filters?.with_emailed_at?.value, filters?.emailed_at_range?.value, props])

    if (standalone) {
        return <>
            <>
                <InverterManufacturerFilter
                    name="manufacturers"
                    id="manufacturers"
                    value={filters.manufacturers.value}
                    onChange={handleChange}
                    summary={summary}
                    data-test="monitoring-index-manufacturers-filter"
                    className="md:min-w-75"
                />
                <MonitoringStatusDropdown
                    name="statuses"
                    id="warranty-statuses"
                    value={filters.statuses.value}
                    onChange={handleChange}
                    summary={summary}
                    data-test="monitoring-index-statuses-filter"
                    className="md:min-w-75"
                />
            </>
        </>
    }
    return <>
        {auth.user?.role.isAdminOrStaffOrAdvisor && <CompanyFilter
            id="certified-provider-id"
            type={UserRoleEnum.CERTIFIED_PROVIDER}
            name="company_id"
            label="Certified Provider"
            placeholder="Any Certified Provider"
            value={filters.company_id.value}
            onChange={handleChange}
            data-test="monitoring-index-certified-provider-id-filter"
            input-size="xs"
        />}

        {auth.user?.role.isAdminOrStaffOrAdvisor &&
            <SearchableSelect
                id="account-manager-email"
                name="account_manager_email"
                label="Account Manager"
                placeholder="Any Account Manager"
                options={accountManagers.map(item => ({
                    value: item.email,
                    title: item.name
                }))}
                value={filters.account_manager_email.value}
                onChange={handleChange}
                data-test="monitoring-index-account-manager-email-filter"
                input-size="xs"
            />}

        <FormControl label="Warranty Type">
            <WarrantyProductTypeFilter
                name="types"
                id="warranty-types"
                value={filters.types.value}
                summary={summary}
                onChange={handleChange}
                data-test="monitoring-index-types-filter"
            />
        </FormControl>

        {auth.user?.role.isAdminOrStaffOrAdvisor &&
            <FormControl label="Company Status">
                <CompanyAccountActivityStatusFilter
                    id="company-account-activity-status"
                    name="company_account_activity_statuses"
                    value={filters.company_account_activity_statuses.value}
                    onChange={handleChange}
                    data-test="company-account-activity-status-filter"
                />
            </FormControl>}

        <FormControl label="System Size">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <Input
                    id="monitoring-system-size-min-filter"
                    name="size_kw_min"
                    placeholder="Min kW"
                    onChange={handleChange}
                    input-size="xs"
                    value={filters.size_kw_min.value !== null ? String(filters.size_kw_min.value) : ''}
                />
                <span className="text-gray-750 text-center">to</span>
                <Input
                    id="monitoring-system-size-max-filter"
                    name="size_kw_max"
                    placeholder="Max kW"
                    onChange={handleChange}
                    input-size="xs"
                    value={filters.size_kw_max.value !== null ? String(filters.size_kw_max.value) : ''}
                />
            </div>
        </FormControl>

        {(auth.user?.role.isAdminOrStaffOrAdvisor ||
                auth.user?.company?.affiliate?.warranty_match_method.isExternalUploadMatch) &&
            <CompanyFilter
                id="affiliate-id"
                type={UserRoleEnum.AFFILIATE}
                name="affiliate_id"
                label="Affiliate"
                placeholder="Any Affiliate"
                value={filters.affiliate_id.value}
                onChange={handleChange}
                disabled={!auth.user?.role.isAdminOrStaffOrAdvisor}
                data-test="monitoring-index-affiliate-id-filter"
                input-size="xs"
            />}

        <DatePicker
            clearable
            id="install-date-range"
            name="install_date_range"
            label="Installation Date"
            placeholder="Select Dates"
            value={filters.install_date_range.value}
            onChange={handleChange}
            singleMode={false}
            maxDate={new Date()}
            data-test="monitoring-index-install-date-range-filter"
            input-size="xs"
        />

        <FormControl label="Emailed Date?" className="border-b border-gray-200 pb-4">
            <div className="flex flex-col gap-2">
                <Checkbox
                    name="with_emailed_at"
                    label="Yes"
                    checked={filters.with_emailed_at.value === '1'}
                    value="1"
                    onChange={handleChange}
                />
                <Checkbox
                    name="with_emailed_at"
                    label="No"
                    checked={filters.with_emailed_at.value === '0'}
                    value="0"
                    onChange={handleChange}
                />
                {filters.with_emailed_at.value === '1' && <DatePicker
                    clearable
                    id="emailed-at-range"
                    name="emailed_at_range"
                    placeholder="Emailed Date"
                    value={filters.emailed_at_range.value}
                    onChange={handleChange}
                    singleMode={false}
                    maxDate={new Date()}
                    data-test="monitoring-index-emailed-at-range-filter"
                />}
            </div>
        </FormControl>

        <ProductFilter
            id="battery-product-id"
            type="batteries"
            name="battery_product_id"
            label="Battery Make"
            placeholder="Any Battery Make"
            value={filters.battery_product_id.value}
            onChange={handleChange}
            data-test="monitoring-index-battery-product-id-filter"
            input-size="xs"
        />

        <FormControl label="Battery Storage">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <Input
                    id="battery-storage-min-filter"
                    name="battery_size_kwh_min"
                    placeholder="Min kWh"
                    onChange={handleChange}
                    input-size="xs"
                    value={filters.battery_size_kwh_min.value !== null ? String(filters.battery_size_kwh_min.value) : ''}
                />
                <span className="text-gray-750 text-center">to</span>
                <Input
                    id="battery-storage-max-filter"
                    name="battery_size_kwh_max"
                    placeholder="Max kWh"
                    onChange={handleChange}
                    input-size="xs"
                    value={filters.battery_size_kwh_max.value !== null ? String(filters.battery_size_kwh_max.value) : ''}
                />
            </div>
        </FormControl>

        <DatePicker
            clearable
            id="detection-date-range"
            name="detection_date_range"
            label="Detection Date"
            placeholder="Select Dates"
            value={filters.detection_date_range.value}
            onChange={handleChange}
            singleMode={false}
            maxDate={new Date()}
            data-test="monitoring-index-detection-date-range-filter"
            input-size="xs"
        />
        <DatePicker
            clearable
            id="last-updated-date-range"
            name="last_updated_date_range"
            label="Last Updated Date"
            placeholder="Select Dates"
            value={filters.last_updated_date_range.value}
            onChange={handleChange}
            singleMode={false}
            maxDate={new Date()}
            data-test="monitoring-index-last-updated-date-range-filter"
            input-size="xs"
        />
    </>
}
